import {
  COUNTRY_CODE,
  DOMESTIC_NUTRITION_KEYS,
} from "../../../Utils/constants.js"

import {
  HONG_KONG_MANDATORY_NUTRIENTS,
  HONG_KONG_NON_MANDATORY_NUTRIENTS,
  HongkongNiTableCalculatedValue,
} from "../../../schema/HongkongSchema/hongkongConstants"

interface NameQuantityItem {
  name: string
  quantity: string | number
}

export function arrayToObj(
  arr: NameQuantityItem[],
): Record<string, string | number> {
  const obj: Record<string, string | number> = {}
  arr.forEach((item) => {
    if (!item.name) return
    obj[item.name] = item.quantity
  })
  return obj
}

function convertToBaseUnit(quantity: number, unit: string): number {
  if (!unit) return 0
  switch (unit.toLowerCase()) {
    case "kg":
    case "l":
      return quantity * 1000
    case "g":
    case "ml":
      return quantity
    default:
      return 0
  }
}

const ROUND_OFF_NUTRIENTS = [
  DOMESTIC_NUTRITION_KEYS.ENERGY?.toLowerCase?.(),
  DOMESTIC_NUTRITION_KEYS.SODIUM?.toLowerCase?.(),
  DOMESTIC_NUTRITION_KEYS.CHOLESTEROL?.toLowerCase?.(),
].filter(Boolean)

function needsRoundOff(nutrientKey: string): boolean {
  const lowerKey = nutrientKey.toLowerCase()

  if (ROUND_OFF_NUTRIENTS.includes(lowerKey)) return true
  return false
}

export function formatPerServeValue(
  nutrientKey: string,
  rawValue: number,
): string {
  if (needsRoundOff(nutrientKey)) {
    return rawValue.toFixed(0)
  } else {
    return rawValue.toFixed(1)
  }
}

export function formatPer100Value(
  nutrientKey: string,
  rawValue: number,
): string {
  if (needsRoundOff(nutrientKey)) {
    return rawValue.toFixed(0)
  } else {
    return rawValue.toFixed(1)
  }
}

function basicPerServeCalc(per100: number, servingSize: number): number {
  return (per100 / 100) * servingSize
}

export function calculateServings(
  serving_size: number,
  serving_size_unit: string,
  net_quantity: number,
  net_quantity_unit: string,
): number {
  const serving_size_in_base = convertToBaseUnit(
    serving_size,
    serving_size_unit,
  )
  const net_quantity_in_base = convertToBaseUnit(
    net_quantity,
    net_quantity_unit,
  )

  if (!serving_size_in_base || !net_quantity_in_base) {
    return 0
  }

  const no_of_servings = net_quantity_in_base / serving_size_in_base
  const fraction = no_of_servings - Math.floor(no_of_servings)
  return fraction >= 0.6
    ? Math.ceil(no_of_servings)
    : Math.floor(no_of_servings)
}

export function getHongkongNiTableCalculatedValue(
  label_json: any,
): HongkongNiTableCalculatedValue {
  if (!label_json) {
    return {
      serving_size: 0,
      country: COUNTRY_CODE.HONG_KONG,
      serving_size_unit: "g",
      no_of_servings: 0,
      servings_per_package: 0,
      mandatory_nutrient_values: {
        ENERGY: {
          key: HONG_KONG_MANDATORY_NUTRIENTS.ENERGY,
          name: "Energy",
          per_serve_kcal: 0,
          per_100g_kcal: 0,
          per_serve_kj: 0,
          per_100g_kj: 0,
          unit_kcal: "kcal",
          unit_kj: "kj",
        },
        CARBOHYDRATES: {
          key: HONG_KONG_MANDATORY_NUTRIENTS.TOTAL_CARBOHYDRATE,
          name: "Carbohydrate",
          per_serve: 0,
          per_100g: 0,
          unit: "g",
        },
        SUGARS: {
          key: HONG_KONG_MANDATORY_NUTRIENTS.TOTAL_SUGARS,
          name: "Sugars",
          per_serve: 0,
          per_100g: 0,
          unit: "g",
        },

        PROTEIN: {
          key: HONG_KONG_MANDATORY_NUTRIENTS.PROTEIN,
          name: "Protein",
          per_serve: 0,
          per_100g: 0,
          unit: "g",
        },
        TOTAL_FAT: {
          key: HONG_KONG_MANDATORY_NUTRIENTS.TOTAL_FAT,
          name: "Total Fat",
          per_serve: 0,
          per_100g: 0,
          unit: "g",
        },
        SATURATED_FAT: {
          key: HONG_KONG_MANDATORY_NUTRIENTS.TOTAL_FAT,
          name: "Saturated Fat",
          per_serve: 0,
          per_100g: 0,
          unit: "g",
        },
        TRANS_FAT: {
          key: HONG_KONG_MANDATORY_NUTRIENTS.TOTAL_FAT,
          name: "Trans Fat",
          per_serve: 0,
          per_100g: 0,
          unit: "g",
        },
        SODIUM: {
          key: HONG_KONG_MANDATORY_NUTRIENTS.SODIUM,
          name: "Trans Fat",
          per_serve: 0,
          per_100g: 0,
          unit: "g",
        },
      },
      non_mandatory_nutrient_values: [],
    }
  }

  const number_of_servings = calculateServings(
    parseFloat(label_json?.serving_size ?? "0"),
    label_json?.serving_size_unit ?? "",
    parseFloat(label_json?.net_quantity ?? "0"),
    label_json?.net_quantity_unit ?? "",
  )

  const non_mandatory_nutrients = arrayToObj(
    label_json?.non_mandatory_nutrient ?? [],
  )
  const serving_size_num = parseFloat(label_json?.serving_size ?? "0")

  const non_mandatory_nutrient_values =
    HONG_KONG_NON_MANDATORY_NUTRIENTS.filter((nm) => {
      const per_100 = parseFloat(
        non_mandatory_nutrients[nm.key?.toLowerCase()]?.toString() || "0",
      )
      return per_100 !== 0
    }).map((nm) => {
      const per_100 = parseFloat(
        non_mandatory_nutrients[nm.key?.toLowerCase()]?.toString() || "0",
      )

      const raw_per_serve = basicPerServeCalc(per_100, serving_size_num)

      const formatted_per_serve = formatPerServeValue(nm.key, raw_per_serve)
      const formatted_per_100_value = formatPer100Value(nm.key, per_100)
      return {
        key: nm.key,
        name: nm.value,
        per_serve: formatted_per_serve,
        per_100g: formatted_per_100_value,
        unit: nm.unit,
      }
    })

  const mandatory_nutrient_arr = label_json?.mandatory_nutrient ?? []
  const first_mandatory = mandatory_nutrient_arr[0] || {}

  function getMandatoryRawPerServe(nutKey: string): number {
    const val = parseFloat(first_mandatory[nutKey] ?? "0")
    return basicPerServeCalc(val, serving_size_num)
  }

  function getMandatoryPerServe(nutKey: string): string {
    return formatPerServeValue(nutKey, getMandatoryRawPerServe(nutKey))
  }

  return {
    serving_size: serving_size_num,
    country: COUNTRY_CODE.HONG_KONG,
    serving_size_unit: label_json?.serving_size_unit ?? "",
    no_of_servings: number_of_servings,
    servings_per_package: number_of_servings,
    mandatory_nutrient_values: {
      ENERGY: {
        key: HONG_KONG_MANDATORY_NUTRIENTS.ENERGY,
        name: "Energy",
        per_serve_kcal: getMandatoryPerServe(
          HONG_KONG_MANDATORY_NUTRIENTS.ENERGY,
        ),
        per_100g_kcal:
          mandatory_nutrient_arr[0][HONG_KONG_MANDATORY_NUTRIENTS.ENERGY],
        per_serve_kj: Math.round(
          +getMandatoryPerServe(HONG_KONG_MANDATORY_NUTRIENTS.ENERGY) * 4.2,
        ),
        per_100g_kj: Math.round(
          mandatory_nutrient_arr[0][HONG_KONG_MANDATORY_NUTRIENTS.ENERGY] * 4.2,
        ),
        unit_kcal: "kcal",
        unit_kj: "kj",
      },
      CARBOHYDRATES: {
        key: HONG_KONG_MANDATORY_NUTRIENTS.TOTAL_CARBOHYDRATE,
        name: "Carbohydrate",
        per_serve: getMandatoryPerServe(
          HONG_KONG_MANDATORY_NUTRIENTS.TOTAL_CARBOHYDRATE,
        ),
        per_100g: parseFloat(
          mandatory_nutrient_arr[0][
            HONG_KONG_MANDATORY_NUTRIENTS.TOTAL_CARBOHYDRATE
          ],
        ).toFixed(1),
        unit: "g",
      },
      SUGARS: {
        key: HONG_KONG_MANDATORY_NUTRIENTS.TOTAL_SUGARS,
        name: "Total Sugars",
        per_serve: getMandatoryPerServe(
          HONG_KONG_MANDATORY_NUTRIENTS.TOTAL_SUGARS,
        ),
        per_100g: parseFloat(
          mandatory_nutrient_arr[0][HONG_KONG_MANDATORY_NUTRIENTS.TOTAL_SUGARS],
        ).toFixed(1),
        unit: "g",
      },

      PROTEIN: {
        key: HONG_KONG_MANDATORY_NUTRIENTS.PROTEIN,
        name: "Protein",
        per_serve: getMandatoryPerServe(HONG_KONG_MANDATORY_NUTRIENTS.PROTEIN),
        per_100g: parseFloat(
          mandatory_nutrient_arr[0][HONG_KONG_MANDATORY_NUTRIENTS.PROTEIN],
        ).toFixed(1),
        unit: "g",
      },
      TOTAL_FAT: {
        key: HONG_KONG_MANDATORY_NUTRIENTS.TOTAL_FAT,
        name: "Fat",
        per_serve: getMandatoryPerServe(
          HONG_KONG_MANDATORY_NUTRIENTS.TOTAL_FAT,
        ),
        per_100g: parseFloat(
          mandatory_nutrient_arr[0][HONG_KONG_MANDATORY_NUTRIENTS.TOTAL_FAT],
        ).toFixed(1),
        unit: "g",
      },
      SATURATED_FAT: {
        key: HONG_KONG_MANDATORY_NUTRIENTS.SATURATED_FAT,
        name: "Fat",
        per_serve: getMandatoryPerServe(
          HONG_KONG_MANDATORY_NUTRIENTS.SATURATED_FAT,
        ),
        per_100g: parseFloat(
          mandatory_nutrient_arr[0][
            HONG_KONG_MANDATORY_NUTRIENTS.SATURATED_FAT
          ],
        ).toFixed(1),
        unit: "g",
      },
      TRANS_FAT: {
        key: HONG_KONG_MANDATORY_NUTRIENTS.TRANS_FAT,
        name: "Fat",
        per_serve: parseFloat(
          getMandatoryPerServe(HONG_KONG_MANDATORY_NUTRIENTS.TOTAL_FAT),
        ).toFixed(1),
        per_100g: parseFloat(
          mandatory_nutrient_arr[0][HONG_KONG_MANDATORY_NUTRIENTS.TRANS_FAT],
        ).toFixed(1),
        unit: "g",
      },
      SODIUM: {
        key: HONG_KONG_MANDATORY_NUTRIENTS.SODIUM,
        name: "Sodium",
        per_serve: getMandatoryPerServe(HONG_KONG_MANDATORY_NUTRIENTS.SODIUM),
        per_100g: Math.round(
          mandatory_nutrient_arr[0][HONG_KONG_MANDATORY_NUTRIENTS.SODIUM],
        ),
        unit: "mg",
      },
    },
    non_mandatory_nutrient_values,
  }
}
